import axiosInstance from "../utils/axiosConfig";
import { useEffect, useState, useMemo } from "react";
import { Dashboard } from "../Dashboard";
import BrAvatar from "./Avatar";
import Grid from "@material-ui/core/Grid";
import { makeStyles } from "@material-ui/core/styles";
import Card from "@material-ui/core/Card";
import Paper from "@material-ui/core/Paper";
import Typography from "@material-ui/core/Typography";
import Messenger from "./Messenger";
import { usePusherConnection } from '../hooks/usePusherConnection';



export default function MessengerPage(props) {
    const { user, theme, isParent } = props;
    const [messages, setMessages] = useState([]);
    const [unreadMessages, setUnreadMessages] = useState([]);
    // The selectedUser is the user you are messaging
    const [selectedUser, setSelectedUser] = useState({});
    const [edit, setEdit] = useState({});
    
    const [message, setMessage] = useState("");
    const [characterCount, setCharacterCount] = useState(0);
    
    const isPusherConnected = usePusherConnection(user);
    
    // Get selected child ID if parent mode
    const selectedChildId = isParent ? window.localStorage.getItem('SELECTED_CHILD_ID') : null;
    
    // Note: Removed premature counter clear on mount to prevent race conditions
    // Counter will be properly updated when messages are actually marked as read
    
    const useStyles = makeStyles({
        root: {
            minHeight: 500,
            maxHeight: 500,
        },
        bullet: {
            minHeight: "100px",
            margin: "0 2px",
            transform: "scale(0.8)",
        },
        conversation: {
            width: "100%",
            overflow: "scroll",
            height: "332px",
            paddingInlineStart: 0,
        },
        title: {
            fontSize: 32,
            fontFamily: "GoodUnicorn",
            color: theme.primaryColor,
        },
        avatar: {
            marginLeft: "30%",
            marginTop: "40%",
            width: "20%",
            height: "20%",
        },
        pos: {
            marginBottom: 12,
        },
        heading: {
            fontSize: 16,
            flexBasis: "33.33%",
            flexShrink: 0,
        },
        chatGrid: {
            marginLeft: "40px",
        },
        playIcon: {
            color: "#eb4c8a",
            cursor: "pointer",
            position: "absolute",
            top: "20%",
            left: "25%",
            transform: "translate(-25%, -25%)",
        },
        imagesCard: {
            backgroundColor: "#d6f1fe",
        },
        images: {
            margin: "4px",
        },
        toggle: {
            margin: "10%",
            borderRadius: "15px",
            backgroundColor: "#fffefe",
            textAlign: "center",
        },
        self: {
            justifyContent: "flex-end",
            alignItems: "flex-end",
        },
        selfChip: {
            background: "linear-gradient(90deg, #ffa0a4 20%, #eb4c8a 80%)",
            color: "white",
        },
        recipientChip: {
            background: "floralwhite",
        },
        homeGrid: {
            "&::before": {
                content: "' '",
                background: `url(${theme.backgroundLogo}) no-repeat center center fixed`,
                opacity: ".6",
                backgroundSize: "cover",
                position: "absolute",
                top: "160px",
                right: "0px",
                bottom: "0px",
                left: "0px",
                zIndex: -1
            }
        }
    });
    
    const classes = useStyles();
    
    useEffect(() => {
        // Create a flag to prevent state updates after component unmount
        let isSubscribed = true;
        
        const fetchMessages = async () => {
            // Only fetch if we have both valid user and selectedUser
            if (!selectedUser?.id) return;
            
            try {
                // Determine which user ID to use for fetching messages
                const sourceUserId = isParent ? selectedChildId : user.id;
                
                if (!sourceUserId) {
                    console.error('No valid user ID available for fetching messages');
                    return;
                }
                
                const result = await axiosInstance.get(
                    `/messages/${sourceUserId}/${selectedUser.id}`
                );
                
                if (!isSubscribed) return; // Prevent state update if component unmounted
                
                if (result.status === 200) {
                    setMessages(result.data);
                    setMessage("");
                    
                    // Find messages that are unread and meant for current user (or selected child)
                    const unreadMessages = result.data.filter(
                        message => message.seen !== 1 && message.recipient_id === (isParent ? parseInt(selectedChildId) : user.id)
                    );
                    
                    // Update unread messages state
                    setUnreadMessages(unreadMessages);
                    
                    // If there are unread messages, mark them as seen
                    if (unreadMessages.length > 0) {
                        try {
                            console.log('MessengerPage: Marking', unreadMessages.length, 'messages as read');
                            
                            // Use the see_messages/me endpoint as in the original code
                            const response = await axiosInstance.post(
                                `/see_messages/me`,
                                { messages: unreadMessages }
                            );
                            
                            console.log('MessengerPage: Mark as read response:', response.data);
                            
                            // Update local message state to reflect seen status
                            if (isSubscribed) {
                                setMessages(prevMessages => 
                                    prevMessages.map(msg => 
                                        unreadMessages.some(unread => unread.id === msg.id)
                                            ? { ...msg, seen: 1 }
                                            : msg
                                    )
                                );
                            }
                            
                            // Trigger custom event to notify Dashboard with server-provided count
                            // Fixed race condition: use server response directly instead of calculation
                            // Add small delay to ensure database update is fully processed
                            // This helps prevent race conditions in staging/production environments
                            setTimeout(() => {
                                const messagesReadEvent = new CustomEvent('messagesRead', {
                                    detail: {
                                        updatedCount: response.data?.current_unread_count !== undefined ? 
                                            response.data.current_unread_count : 0,
                                        markedAsRead: response.data?.updated_count || unreadMessages.length,
                                        fromServer: true, // Flag indicating this count comes directly from server
                                        timestamp: Date.now() // Add timestamp for debugging
                                    }
                                });
                                console.log('MessengerPage: Dispatching messagesRead event with server count:', messagesReadEvent.detail);
                                window.dispatchEvent(messagesReadEvent);
                            }, 100); // 100ms delay to allow database to process update
                            
                        } catch (error) {
                            console.error('MessengerPage: Failed to mark messages as seen:', error);
                        }
                    }
                }
            } catch (error) {
                console.error('Failed to fetch messages:', error);
            }
        };
        
        fetchMessages();
        
        // Cleanup function
        return () => {
            isSubscribed = false;
        };
        
    }, [selectedUser?.id, user?.id, selectedChildId, isParent]);
    
    const sendMessage = async () => {
        // If parent user, don't allow sending messages
        if (isParent) {
            console.log("Parents cannot send messages");
            return;
        }

        if(edit.id){
            const result = await axiosInstance.put(
                `/messages/${edit.id}`,
                { content: message }
            );
            if (result.status === 200) {
                messages.map( (item) => { if(item.id === result.data.id){ item.content = result.data.content; } return item; });
                setMessage("");
                setCharacterCount(0);
                setEdit({});
                setMessages([...messages]);
            }
        } else {
            const result = await axiosInstance.post(
                `/send_message/${selectedUser.id}`,
                { content: message }
            );
            if (result.status === 201) {
                // Ensure we're using the server's timestamp
                const newMessage = {
                    sender_id: user.id,
                    recipient_id: selectedUser.id,
                    content: message,
                    id: result.data.message.id,
                    created_at: result.data.message.created_at // Use server timestamp
                };
                
                setMessages(prevMessages => [...prevMessages, newMessage]);
                setMessage("");
                setCharacterCount(0);
                setFirstMessages(prev => ({
                    ...prev,
                    [selectedUser.id]: newMessage
                }));
            }
        }
    };
    
    const handleMessageInput = (e) => {
        // If parent user, don't allow message input
        if (isParent) return;

        if (e.keyCode == 13) {
            return sendMessage();
        }
        setMessage(e.target.value.slice(0, 240));
        setCharacterCount(e.target.value.slice(0, 240).length);
    };
    
    const handleEdit = (message) => {
        // If parent user, don't allow editing messages
        if (isParent) return;
        
        setMessage(message.content);
        setEdit(message);
    };
    
    const handleRemove = async (message) => {
        // If parent user, don't allow removing messages
        if (isParent) return;
        
        const result = await axiosInstance.delete(
            `/messages/${message.id}`
        );
        setMessages(messages.filter(item => item.id !== message.id));
    }
    
    const [firstMessages, setFirstMessages] = useState({});
    
    const XCPalRows = useMemo(
        () => (props) => {
            const { friends, selectedUser, setSelectedUser, classes, theme } = props;
            return friends.map((pal) => (
                <XCPalRow
                    key={pal.id}
                    user={pal}
                    selectedUser={selectedUser}
                    firstMessages={firstMessages}
                    setSelectedUser={setSelectedUser}
                    classes={classes}
                    theme={theme}
                />
            ));
        },
        [selectedUser, firstMessages]
    );
    
    const XCPalRow = useMemo(
        () => (props) => {
                const {
                user,
                firstMessages,
                setSelectedUser,
                selectedUser,
                classes,
                theme
            } = props;
            
            return (
                <Paper
                    elevation={0}
                    style={
                        selectedUser && selectedUser.id === user.id
                            ? { backgroundColor: "#f1f1f1" }
                            : {}
                    }
                    className={classes.palrow}
                    onClick={() => setSelectedUser(user)}
                >
                    <Grid container spacing={2}>
                        <Grid item xs={3}>
                            <BrAvatar user={user} classes={classes} theme={theme} />
                        </Grid>
                        <Grid item xs={9}>
                            <Typography className={classes.xcName}>
                                {" "}
                                {props.user.name}{" "}
                            </Typography>
                            <Typography
                                style={{
                                    maxHeight: "100px",
                                    overflow: "auto",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                }}
                            >
                            {" "}
                            {firstMessages[user.id]
                                ? firstMessages[user.id].content
                                : ""}{" "}
                            </Typography>
                        </Grid>
                    </Grid>
                </Paper>
            );
        },
        [classes, firstMessages, selectedUser]
    );
    
    const [friends, setFriends] = useState([]);
    
    // First useEffect for initial data fetching - runs once on mount
    useEffect(() => {
        if (!user?.id) return;
        let mounted = true;
        
        const fetchInitialData = async () => {
            try {
                // For parent users, fetch their selected child's friends and messages
                const userId = isParent ? selectedChildId : user.id;
                
                if (!userId) {
                    console.error('No valid user ID available for fetching friends');
                    return;
                }
                
                const [friendsResult, firstMessagesResult] = await Promise.all([
                    axiosInstance.get(`/friends/${userId}`),
                    axiosInstance.get(`/first_messages/${userId}`)
                ]);
                
                if (!mounted) return;
                
                // Set friends
                setFriends(friendsResult.data);
                
                // Set selected user from URL hash or first friend
                const hashId = window.location.hash.slice(1);
                const selectedFriend = friendsResult.data.find(
                    friend => friend.id.toString() === hashId
                ) || friendsResult.data[0] || {};
                
                setSelectedUser(selectedFriend);
                
                // Set first messages
                if (friendsResult.data?.length) {
                    const messagesMap = {};
                    friendsResult.data.forEach(friend => {
                        messagesMap[friend.id] = firstMessagesResult.data.find(
                            msg => msg.recipient_id === friend.id || msg.sender_id === friend.id
                        );
                    });
                    setFirstMessages(messagesMap);
                }
            } catch (error) {
                console.error('Initial data fetch failed:', error);
            }
        };
        
        fetchInitialData();
        
        return () => {
            mounted = false;
        };
    }, [user?.id, selectedChildId, isParent]); // Added selectedChildId and isParent as dependencies
    
    // Separate useEffect for Pusher setup
    useEffect(() => {
        if (!isPusherConnected || !user?.id) return;
        let pusherChannel = null;
        
        const setupPusherChannel = () => {
            if (window.Echo) {
                // For parent users, listen to the selected child's channel
                const channelUserId = isParent ? selectedChildId : user.id;
                
                if (!channelUserId) {
                    console.error('No valid user ID available for Pusher channel');
                    return;
                }
                
                pusherChannel = window.Echo.private(`messages.${channelUserId}`);
                
                pusherChannel.listen('.SentMessage', (event) => {
                    if (!event.message) return;
                    
                    // Determine the current user ID (accounting for parent impersonation)
                    const currentUserId = isParent ? parseInt(selectedChildId) : user.id;
                    
                    // Check if this message is part of the currently viewed conversation
                    const isCurrentConversation = event.message.sender_id === selectedUser?.id || 
                        event.message.recipient_id === selectedUser?.id;
                    
                    // Check if this message is for the current user (incoming message)
                    const isIncomingMessage = event.message.recipient_id === currentUserId;
                    
                    if (isCurrentConversation) {
                        // Add message to the conversation
                        setMessages(prev => {
                            if (prev.some(msg => msg.id === event.message.id)) return prev;
                            const newMessages = [...prev, event.message];
                            return newMessages.sort((a, b) => 
                                new Date(a.created_at) - new Date(b.created_at)
                            );
                        });
                        setFirstMessages(prev => ({
                            ...prev,
                            [event.message.sender_id]: event.message
                        }));
                        
                        // CRITICAL: If user is actively viewing this chat and receives an incoming message,
                        // automatically mark it as read immediately (no badge should appear)
                        if (isIncomingMessage && selectedUser?.id && event.message.seen !== 1) {
                            console.log('MessengerPage: Auto-marking incoming message as read (user is viewing chat)', {
                                messageId: event.message.id,
                                senderId: event.message.sender_id,
                                recipientId: event.message.recipient_id,
                                currentUserId: currentUserId,
                                selectedUserId: selectedUser.id
                            });
                            
                            // Mark message as read immediately without waiting
                            (async () => {
                                try {
                                    const response = await axiosInstance.post(
                                        `/see_messages/me`,
                                        { messages: [event.message] }
                                    );
                                    
                                    console.log('MessengerPage: Auto-marked message as read response:', response.data);
                                    
                                    // Update local message state to reflect seen status
                                    setMessages(prevMessages => 
                                        prevMessages.map(msg => 
                                            msg.id === event.message.id
                                                ? { ...msg, seen: 1 }
                                                : msg
                                        )
                                    );
                                    
                                    // Update unread messages state
                                    setUnreadMessages(prev => prev.filter(msg => msg.id !== event.message.id));
                                    
                                    // Dispatch messagesRead event to update Dashboard counter
                                    // Use server-provided count for accuracy
                                    const messagesReadEvent = new CustomEvent('messagesRead', {
                                        detail: {
                                            updatedCount: response.data?.current_unread_count !== undefined ? 
                                                response.data.current_unread_count : 0,
                                            markedAsRead: response.data?.updated_count || 1,
                                            fromServer: true,
                                            timestamp: Date.now(),
                                            autoMarked: true // Flag indicating this was auto-marked while viewing
                                        }
                                    });
                                    console.log('MessengerPage: Dispatching messagesRead event for auto-marked message:', messagesReadEvent.detail);
                                    window.dispatchEvent(messagesReadEvent);
                                    
                                } catch (error) {
                                    console.error('MessengerPage: Failed to auto-mark message as read:', error);
                                    // Don't update local state if API call fails
                                }
                            })();
                        }
                    }
                });
            }
        };
        
        setupPusherChannel();
        
        return () => {
            if (pusherChannel) {
                pusherChannel.stopListening('.SentMessage');
            }
        };
    }, [isPusherConnected, user?.id, selectedUser?.id, selectedChildId, isParent]);
    
    return (
        <>
        <Dashboard initialTab={6} user={user} theme={theme} isParent={isParent} />
            <div className={classes.homeGrid}>
                <Grid container spacing={2}>
                    <Grid item xs={1} />
                    <Grid className={classes.title} style={{ marginTop: "1.5rem" }} item xs={11}>
                        {isParent ? "Child's Messages" : "Chat"}
                    </Grid>
                    <Grid item xs={1} />
                    <Grid item xs={3} className={classes.palGrid}>
                        <Card className={classes.root}>
                            <Paper className={classes.toggle}></Paper>
                            
                            <XCPalRows
                                friends={friends}
                                firstMessages={firstMessages}
                                selectedUser={selectedUser}
                                setSelectedUser={setSelectedUser}
                                classes={classes}
                                theme={theme}
                            />
                        </Card>
                    </Grid>
                    <Grid item xs={6} className={classes.chatGrid}>
                        <Messenger
                            characterCount={characterCount}
                            messages={messages}
                            message={message}
                            selectedUser={selectedUser}
                            sendMessage={sendMessage}
                            handleMessageInput={handleMessageInput}
                            handleEdit={handleEdit}
                            handleRemove={handleRemove}
                            edit={edit}
                            theme={theme}
                            user={user}
                            isParent={isParent}
                        />
                    </Grid>
                </Grid>
            </div>
        </>
    );
};